import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ArrowsClockwise, Check, Plugs} from '../../icons';
import {Spinner} from '../../Spinner';
import {YesNoModal} from '../../elements/YesNoModal';
import {ClientEconomicsApi} from '../../../api/clientEconomicsApi';
import {EscalafyChannelApi} from '../../../api/escalafyChannelApi';
import {ESCALAFY_STATUS_KEYS, syncWhenLabel} from './EscalafySourceStrip';
import {FORM_FIELDS} from './state/economiaModel';
import {anyInProgress, finishedConnections, lastAttemptFailed, syncStamps} from './state/escalafyProvenance';
import {
    AGENCY_CONNECTIONS_PATH,
    escalafyRedirectUri,
    mergeMapping,
    needsRoleCallout,
    orgInitials,
    retryAfterMinutes,
    selectedLinks,
    setPendingEscalafyConnection,
} from './state/escalafyModel';

export const STEP_CONNECT = 1;
export const STEP_ORGS = 2;
export const STEP_WHAT = 3;

// Un doble clic en «Continuar» caía, con el segundo clic, en «Vincular» —el botón nuevo queda en el
// MISMO lugar— y vinculaba sin mostrar el paso 3 (R-ecofe-07). El botón de un paso recién abierto no
// responde durante este lapso: nadie lee el paso y decide en menos.
export const STEP_ARM_DELAY_MS = 500;

const STEPS = [
    {step: STEP_CONNECT, key: 'economics.escalafy.step.connect'},
    {step: STEP_ORGS, key: 'economics.escalafy.step.orgs'},
    {step: STEP_WHAT, key: 'economics.escalafy.step.what'},
];

// Por qué una organización no se puede usar (§5.2). Un motivo nuevo cae al de «sin datos».
const UNUSABLE_REASONS = new Set(['LIMITED_ROLE', 'PLAN_WITHOUT_API', 'NO_DATA']);

const ROLE_KEYS = {
    admin: 'economics.escalafy.orgs.role.admin',
    media_buyer: 'economics.escalafy.orgs.role.media_buyer',
};

// Conexiones que se arreglan RECONECTANDO su credencial (el token ya no sirve o no ve la organización):
// ahí no se ofrece «Actualizar ahora» —fallaría— sino «Reconectar» (R-ecofe-04).
const NEEDS_RECONNECT = new Set(['DISCONNECTED', 'ORG_NOT_FOUND']);

// Vincular y «Actualizar ahora» sólo ENCOLAN la sincronización: la que deriva el perfil corre unos
// segundos (o minutos) después. Recargar la pantalla en el momento del vínculo trae el perfil de ANTES,
// y el formulario quedaba vacío aunque Escalafy ya hubiera traído el margen (primera conexión real,
// Gótica, 30/09: «esto no lo levanta Escalafy», y sí lo levantaba). Se espera a que cambie la última
// sincronización de una conexión y recién ahí se recarga, cada `SYNC_POLL_MS` y hasta `SYNC_POLL_TRIES`
// veces (2 minutos). Si no llega, la fila deja de decir «Actualizando…» y no se avisa nada. «Terminó» es
// el fin del INTENTO (`syncStamp`), no el último dato bueno: un intento que falla no mueve el dato, y la
// pantalla esperaba en vano (Gótica, 01/10).
export const SYNC_POLL_MS = 4000;
export const SYNC_POLL_TRIES = 30;
// Pasados los dos minutos se sigue preguntando mientras el backend diga `sync_in_progress` (lo sostiene hasta
// 10 min): cortar antes dejaba la fila y la franja en «Actualizando…» sin nadie que volviera a preguntar
// (R-escalafy-02). Esto es la red por si el backend no lo soltara nunca: 11 minutos.
export const SYNC_POLL_MAX_TRIES = Math.ceil((11 * 60 * 1000) / SYNC_POLL_MS);

function ownConnections(response, clientId) {
    const list = listOf(response, 'connections') || [];
    // El filtro va también acá: si el backend ignorara el parámetro, esta pantalla no puede listar
    // conexiones de OTRO cliente.
    return list.filter((connection) => connection && connection.client
        && String(connection.client.id) === String(clientId));
}

function listOf(response, key) {
    if (!response || response.status !== 'OK') return null;
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data[key])) return data[key];
    return null;
}

function errorCodeOf(response) {
    const body = (response && response.raw_body) || {};
    return (response && response.error_code) || body.error_code || (response && response.status);
}

/**
 * Las conexiones de Escalafy que ya están en Conexiones y sirven para elegir organizaciones desde acá (el
 * paso 0 del asistente): las de la agencia y vivas. Las que hizo un cliente desde el portal no: el backend
 * no le muestra a la agencia lo que ve el token del dueño.
 */
export function usableAgencyCredentials(connectionsResponse) {
    const data = connectionsResponse && connectionsResponse.status === 'OK' ? connectionsResponse.data : null;
    const list = data && Array.isArray(data.escalafy_credentials) ? data.escalafy_credentials : [];
    return list.filter((credential) => credential && credential.credential_id
        && credential.status !== 'DISCONNECTED' && !credential.managed_by_client);
}

/**
 * ¿La conexión ya es un vínculo del sistema de cuentas (contrato «Escalafy como canal» §3)? Entonces
 * quitarla DESVINCULA la organización del cliente y la credencial sigue en Conexiones. Con un backend
 * anterior, quitarla revocaba la credencial: ahí sigue diciendo «Desconectar».
 */
export function isChannelLink(connection) {
    return !!connection && connection.agency_client_ad_account_id != null;
}

export function connectionNeedsReconnect(connection) {
    return !!connection && (NEEDS_RECONNECT.has(connection.status)
        || (!!connection.credential && connection.credential.status === 'DISCONNECTED'));
}

/** El texto de una conexión que NO está OK (`null` si lo está): el mismo de la franja y de la ficha. */
export function connectionStatusKey(connection) {
    if (!connection) return null;
    return ESCALAFY_STATUS_KEYS[connection.status]
        || (connectionNeedsReconnect(connection) ? ESCALAFY_STATUS_KEYS.DISCONNECTED : null);
}

// El detalle de un error del vínculo viaja suelto (`POST agency/escalafy/connections/`) o, en las views del sistema
// de cuentas (`client/<id>/ad_account/add`, el select del portal), dentro de `errors` como un STRING JSON.
function errorDetail(body) {
    let extra = {};
    if (typeof body.errors === 'string') {
        try {
            extra = JSON.parse(body.errors) || {};
        } catch (error) {
            extra = {};
        }
    } else if (body.errors && typeof body.errors === 'object') {
        extra = body.errors;
    }
    return {...extra, ...body};
}

/**
 * El `ORG_ALREADY_LINKED` nombra al cliente de LA MISMA agencia que ya la tiene (entre agencias
 * el backend no dice nada: aislamiento). Cualquier otro error va al genérico, nunca el texto crudo.
 */
export function linkErrorMessage(response, t) {
    const body = errorDetail((response && response.raw_body) || {});
    const code = errorCodeOf(response);
    if (code === 'ORG_ALREADY_LINKED') {
        // `linked_to.client_name` viene en null si es un cliente que este usuario no ve: se dice que
        // está tomada, no a cuál.
        const client = body.linked_to && body.linked_to.client_name;
        if (!client) return t('economics.escalafy.error.org_already_linked_other');
        return t('economics.escalafy.error.org_already_linked', {client, interpolation: {escapeValue: false}});
    }
    if (code === 'ORG_NOT_USABLE' && UNUSABLE_REASONS.has(body.unusable_reason)) {
        return t(`economics.escalafy.orgs.unusable.${body.unusable_reason}`);
    }
    if (code === 'CONNECTOR_NOT_CONFIGURED') return t('economics.escalafy.error.not_configured');
    // El cupo de cuentas vinculadas del plan: una organización cuenta como cualquier cuenta.
    if (code === 'CANT_ADD_MORE_AD_ACCOUNTS') return t('economics.escalafy.error.cant_add_more_accounts');
    return t('economics.error.generic');
}

/** El toast de «Actualizar ahora» fallido. */
export function syncErrorMessage(response, t) {
    const code = errorCodeOf(response);
    if (code === 'SYNC_RATE_LIMITED') {
        const minutes = retryAfterMinutes(response);
        if (minutes !== null) return t('economics.escalafy.error.sync_rate_limited', {count: minutes});
    }
    if (code === 'ESCALAFY_DISCONNECTED') return t('client_panel.economy.escalafy.disconnected');
    return t('economics.error.generic');
}

/**
 * Asistente «Conectar Escalafy» (mockup 3): conectar → elegir organizaciones → qué se importa.
 *
 * El paso 1 va a Escalafy EN LA MISMA PESTAÑA (OAuth 2.1 con PKCE; el backend arma la URL y guarda
 * el `code_verifier`). La vuelta entra por `/escalafy/callback` (ruta fija, registrada en Escalafy),
 * que postea `code` + `state` y trae de nuevo a esta pantalla con `?escalafy_credential=<id>`: el
 * asistente reabre en el paso 2 con las organizaciones EN VIVO, así que volver después de que el
 * cliente suba el rol muestra la organización ya disponible.
 *
 * Con `reconnectCredentialId` es una RECONEXIÓN (el «Reconectar» de una conexión caída, el ÚNICO
 * camino que la manda): el paso 1 pide entrar con la misma cuenta, y la vuelta no abre el paso 2.
 *
 * `agencyCredentials` son las conexiones de Escalafy que ya están en Conexiones (`usableAgencyCredentials`;
 * `null` mientras se piden). Con alguna, el paso 1 empieza por ofrecerlas (el «paso 0»): elegir una va derecho
 * a sus organizaciones, sin volver a pasar por Escalafy; «Conectar otra cuenta» sigue con el OAuth de siempre.
 */
export function EscalafyConnectWizard({isOpen, onClose, clientId, clients = [], credentialId = null,
                                          reconnectCredentialId = null, agencyCredentials = [], onLinked}) {
    const {t} = useTranslation();
    // La credencial cuyas organizaciones se eligen: la de la vuelta del OAuth, o la que se eligió en el paso 0.
    const [activeCredentialId, setActiveCredentialId] = useState(credentialId);
    const [step, setStep] = useState(credentialId ? STEP_ORGS : STEP_CONNECT);
    const [connecting, setConnecting] = useState(false);
    const [connectError, setConnectError] = useState(null);
    const [orgs, setOrgs] = useState({loading: false, failed: false, list: null, email: null});
    const [mapping, setMapping] = useState({});
    const [linking, setLinking] = useState(false);
    const bodyRef = useRef(null);
    const stepOpenedAt = useRef(0);
    const shownStep = useRef(step);

    const loadOrgs = useCallback(() => {
        if (!activeCredentialId) return;
        setOrgs({loading: true, failed: false, list: null, email: null});
        ClientEconomicsApi.escalafyOrganizations(activeCredentialId).then((response) => {
            const list = listOf(response, 'organizations');
            const email = (response && response.data && response.data.escalafy_email) || null;
            setOrgs({loading: false, failed: list === null, list, email});
            if (list) setMapping((current) => mergeMapping(list, current, clientId));
        }).catch(() => setOrgs({loading: false, failed: true, list: null, email: null}));
    }, [activeCredentialId, clientId]);

    useEffect(() => {
        if (isOpen && step === STEP_ORGS && activeCredentialId && orgs.list === null && !orgs.loading && !orgs.failed) {
            loadOrgs();
        }
    }, [isOpen, step, activeCredentialId, orgs, loadOrgs]);

    const chooseExistingCredential = (credential) => {
        setActiveCredentialId(credential.credential_id);
        setOrgs({loading: false, failed: false, list: null, email: null});
        setStep(STEP_ORGS);
    };

    // Al cambiar de paso, el foco va al cuerpo del paso nuevo (lo anuncia el lector de pantalla y el
    // botón que se apretó ya no existe). La apertura no lo mueve: eso lo hace el modal.
    useEffect(() => {
        stepOpenedAt.current = Date.now();
        if (shownStep.current === step) return;
        shownStep.current = step;
        if (bodyRef.current && bodyRef.current.focus) bodyRef.current.focus();
    }, [step]);

    const armed = () => Date.now() - stepOpenedAt.current >= STEP_ARM_DELAY_MS;

    const startOAuth = () => {
        setConnecting(true);
        setConnectError(null);
        const redirectUri = escalafyRedirectUri();
        ClientEconomicsApi.escalafyAuthUrl(redirectUri, reconnectCredentialId).then((response) => {
            const url = response && response.status === 'OK' && response.data && response.data.url;
            if (url) {
                setPendingEscalafyConnection({clientId});
                window.location.assign(url);
                return;
            }
            setConnecting(false);
            const notConfigured = response && (response.http_status === 503
                || errorCodeOf(response) === 'CONNECTOR_NOT_CONFIGURED');
            setConnectError(t(notConfigured ? 'economics.escalafy.error.not_configured' : 'economics.error.generic'));
        }).catch(() => {
            setConnecting(false);
            setConnectError(t('economics.error.generic'));
        });
    };

    const links = selectedLinks(orgs.list, mapping);

    const finish = async () => {
        if (linking || !armed()) return;
        setLinking(true);
        let linked = 0;
        for (const link of links) {
            // En serie: el backend encola un sync por conexión y Escalafy limita a 60 req/min.
            // eslint-disable-next-line no-await-in-loop
            const response = await ClientEconomicsApi.escalafyLink({
                credentialId: activeCredentialId, orgId: link.orgId, agencyClientId: link.agencyClientId,
            }).catch(() => null);
            if (response && response.status === 'OK') {
                linked += 1;
            } else {
                toast.error(linkErrorMessage(response, t));
            }
        }
        setLinking(false);
        if (linked > 0 && onLinked) onLinked();
        if (linked === links.length) {
            toast.success(t('economics.escalafy.sync_queued'));
            onClose();
            return;
        }
        // Alguna no se vinculó (R-ecofe-K04): el asistente NO se cierra como si hubiera salido bien.
        // Vuelve a las organizaciones, recargadas: las vinculadas salen de la lista y las que faltan
        // conservan el cliente elegido.
        if (linked > 0) toast.warning(t('economics.escalafy.partial'));
        setOrgs({loading: false, failed: false, list: null, email: orgs.email});
        setStep(STEP_ORGS);
    };

    const renderSteps = () => (
        <ol className="eco-wiz__steps">
            {STEPS.map(({step: number, key}) => {
                const state = number < step ? '-done' : (number === step ? '-cur' : '');
                return (
                    <li key={number} className={`eco-wiz__step ${state}`} aria-current={number === step ? 'step' : undefined}>
                        <span className="eco-wiz__n">{number < step ? <Check size={11} weight="bold"/> : number}</span>
                        {t(key)}
                    </li>
                );
            })}
        </ol>
    );

    // El paso 0: usar una conexión que ya está en Conexiones (una por botón, con su nombre) o conectar otra.
    // Una reconexión no lo muestra: ahí hay que entrar con la cuenta caída.
    const renderChooseCredential = () => (
        <div className="eco-wiz__oauth eco-wiz__existing" data-testid="eco-escalafy-existing">
            <span className="eco-wiz__logos" aria-hidden="true"><Plugs size={22} weight="bold"/></span>
            <h3 className="eco-wiz__subtitle">{t('economics.escalafy.use_existing')}</h3>
            <ul className="eco-wiz__creds">
                {agencyCredentials.map((credential) => (
                    <li key={credential.credential_id}>
                        <button type="button" className="btn -alt eco-wiz__cta"
                                data-testid={`eco-escalafy-use-${credential.credential_id}`}
                                onClick={() => chooseExistingCredential(credential)}>
                            {credential.label || t('credential_row.unknown_label')}
                        </button>
                    </li>
                ))}
            </ul>
            <button type="button" className="eco-link" onClick={startOAuth} disabled={connecting}
                    aria-busy={connecting || undefined} data-testid="eco-escalafy-connect-new">
                {connecting ? <Spinner color="positive very-small"/> : <Plugs size={13} weight="bold"/>}
                {t('economics.escalafy.connect_new')}
            </button>
            {connectError && <p className="eco-wiz__error" role="alert">{connectError}</p>}
        </div>
    );

    const renderConnect = () => {
        if (reconnectCredentialId) return renderOAuth();
        // Todavía no se sabe si hay conexiones para ofrecer: mostrar el OAuth y cambiarlo después sería un salto.
        if (agencyCredentials === null) return <div className="box-loader"><Spinner color="positive small"/></div>;
        return agencyCredentials.length > 0 ? renderChooseCredential() : renderOAuth();
    };

    const renderOAuth = () => (
        <div className="eco-wiz__oauth">
            <span className="eco-wiz__logos" aria-hidden="true"><Plugs size={22} weight="bold"/></span>
            <p>{t('economics.escalafy.intro')}</p>
            {reconnectCredentialId && (
                <p className="eco-callout" data-testid="eco-escalafy-reconnect-hint">
                    {t('economics.escalafy.reconnect_hint')}
                </p>
            )}
            <button type="button" className="btn -alt eco-wiz__cta" onClick={startOAuth} disabled={connecting}
                    aria-busy={connecting || undefined} data-testid="eco-escalafy-oauth">
                {connecting && <Spinner color="negative very-small"/>}
                {t(reconnectCredentialId ? 'economics.escalafy.reconnect' : 'economics.escalafy.step.connect')}
            </button>
            {connectError && <p className="eco-wiz__error" role="alert">{connectError}</p>}
        </div>
    );

    // El cliente del mapeo inicial puede no estar en la lista (todavía cargando, o inactivo): sin su
    // opción, el select mostraba «Elegir cliente» con el vínculo ya elegido por debajo (R-ecofe-29).
    const renderClientOptions = (value) => {
        const missing = value && !clients.some((client) => String(client.id) === String(value));
        return (
            <>
                <option value="">{t('economics.escalafy.orgs.select_client')}</option>
                {missing && (
                    <option value={value}>
                        {String(value) === String(clientId) ? t('economics.escalafy.orgs.current_client') : value}
                    </option>
                )}
                {clients.map((client) => (
                    <option key={client.id} value={String(client.id)}>{client.name}</option>
                ))}
            </>
        );
    };

    const renderOrgRight = (org) => {
        if (org.linked_to) {
            const name = org.linked_to.client_name;
            return (
                <span className="eco-org__state">
                    {name
                        ? t('economics.escalafy.orgs.linked_to', {client: name, interpolation: {escapeValue: false}})
                        : t('economics.escalafy.orgs.linked_to_other')}
                </span>
            );
        }
        if (org.usable !== true) {
            const reason = UNUSABLE_REASONS.has(org.unusable_reason) ? org.unusable_reason : 'NO_DATA';
            return <span className="eco-org__state -off">{t(`economics.escalafy.orgs.unusable.${reason}`)}</span>;
        }
        const value = mapping[org.org_id] || '';
        return (
            <select id={`eco-org-${org.org_id}`} className="eco-org__select" value={value}
                    aria-label={t('economics.escalafy.orgs.select_client_for', {
                        org: org.name || '—', interpolation: {escapeValue: false},
                    })}
                    onChange={(event) => setMapping((current) => ({...current, [org.org_id]: event.target.value}))}>
                {renderClientOptions(value)}
            </select>
        );
    };

    const renderOrgs = () => {
        if (orgs.loading || (orgs.list === null && !orgs.failed)) {
            return <div className="box-loader"><Spinner color="positive small"/></div>;
        }
        if (orgs.failed) {
            return (
                <p className="eco-wiz__error" role="alert">
                    {t('economics.error.generic')}{' '}
                    <button type="button" className="eco-link" onClick={loadOrgs}>{t('client_panel.error_retry')}</button>
                </p>
            );
        }
        return (
            <>
                {orgs.email && (
                    <p className="eco-wiz__as">
                        {t('economics.escalafy.connected_as', {email: orgs.email, interpolation: {escapeValue: false}})}
                    </p>
                )}
                <h3 className="eco-wiz__subtitle">{t('economics.escalafy.orgs.title')}</h3>
                {!orgs.list.length ? (
                    <p className="eco-wiz__empty" data-testid="eco-escalafy-orgs-empty">
                        {t('economics.escalafy.orgs.empty')}
                    </p>
                ) : (
                    <ul className="eco-orgs" data-testid="eco-escalafy-orgs">
                        {orgs.list.map((org) => (
                            <li key={org.org_id} data-testid={`eco-org-${org.org_id}`}
                                className={`eco-org${org.usable === true && !org.linked_to ? ' -ok' : ' -no'}`}>
                                <span className="eco-org__av" aria-hidden="true">{orgInitials(org.name)}</span>
                                <span className="eco-org__txt">
                                    <span className="eco-org__name">{org.name}</span>
                                    {org.plan && <span className="eco-org__sub">{org.plan}</span>}
                                </span>
                                {ROLE_KEYS[org.role] && (
                                    <span className={`eco-role -${org.role}`}>{t(ROLE_KEYS[org.role])}</span>
                                )}
                                {renderOrgRight(org)}
                            </li>
                        ))}
                    </ul>
                )}
                {needsRoleCallout(orgs.list) && (
                    <p className="eco-callout" data-testid="eco-escalafy-callout">
                        {t('economics.escalafy.orgs.callout_role')}
                    </p>
                )}
            </>
        );
    };

    const renderWhat = () => (
        <div className="eco-wiz__what">
            <h3 className="eco-wiz__subtitle">{t('economics.escalafy.what.title')}</h3>
            <ul className="eco-wiz__items">
                {String(t('economics.escalafy.what.items')).split(' · ').map((item) => <li key={item}>{item}</li>)}
            </ul>
        </div>
    );

    // Cada botón con su `key` (paso + acción): sin ella React reusaba el mismo <button> entre pasos.
    const renderFooter = () => {
        if (step === STEP_CONNECT) {
            return (
                <button key="cancel-connect" type="button" className="btn -negative" onClick={onClose}>
                    {t('economics.form.cancel')}
                </button>
            );
        }
        if (step === STEP_ORGS) {
            return (
                <>
                    <button key="cancel-orgs" type="button" className="btn -negative" onClick={onClose}>
                        {t('economics.form.cancel')}
                    </button>
                    <button key="continue" type="button" className="btn -alt" disabled={!links.length}
                            data-testid="eco-escalafy-continue"
                            onClick={() => { if (armed()) setStep(STEP_WHAT); }}>
                        {t('economics.escalafy.continue')}
                    </button>
                </>
            );
        }
        return (
            <>
                <button key="back" type="button" className="btn -negative" onClick={() => setStep(STEP_ORGS)}
                        disabled={linking}>
                    {t('economics.escalafy.back')}
                </button>
                <button key="finish" type="button" className="btn -alt" disabled={!links.length || linking}
                        aria-busy={linking || undefined} data-testid="eco-escalafy-finish" onClick={finish}>
                    {linking && <Spinner color="negative very-small"/>}
                    {t('economics.escalafy.finish', {count: links.length})}
                </button>
            </>
        );
    };

    return (
        <Modal isOpen={isOpen} toggle={linking ? undefined : onClose} className="eco-wiz-modal" size="lg"
               labelledBy="eco-wiz-title">
            <ModalHeader toggle={linking ? undefined : onClose} id="eco-wiz-title">
                {t('economics.escalafy.title')}
            </ModalHeader>
            <ModalBody>
                <div className="eco-wiz">
                    {renderSteps()}
                    <div className="eco-wiz__body" ref={bodyRef} tabIndex={-1} data-testid="eco-escalafy-body"
                         aria-label={t(STEPS[step - 1].key)}>
                        {step === STEP_CONNECT && renderConnect()}
                        {step === STEP_ORGS && renderOrgs()}
                        {step === STEP_WHAT && renderWhat()}
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>{renderFooter()}</ModalFooter>
        </Modal>
    );
}

/**
 * La sección «Conectar Escalafy» al pie de la pantalla de economía: las conexiones de ESTE cliente
 * (estado, última actualización, «Actualizar ahora», «Reconectar», «Desconectar») y la puerta al
 * asistente.
 *
 * Cada fila dice si se está actualizando AHORA (la espera que pidió esta pantalla, o una que el backend
 * sabe en curso: `sync_in_progress`, que sobrevive a un recargo), de cuándo es el último dato bueno y,
 * si el último intento falló, cuándo fue. `onSyncingChange` le avisa a la pantalla, que lo muestra
 * también arriba del formulario.
 *
 * `credentialId` es la vuelta del OAuth: abre el asistente en el paso 2 UNA vez y avisa
 * (`onCredentialUsed`) para que la pantalla lo olvide. El botón general arranca siempre en el paso 1.
 *
 * Desde que Escalafy es un canal, la conexión vive en Conexiones y acá se VINCULA al cliente: «Desvincular»
 * no toca la credencial, el asistente ofrece las conexiones que ya existen y hay un link a Conexiones.
 * `onManageConnections` hace esa navegación dentro de la app; sin él, el link navega como cualquier link
 * (este módulo no importa el router: lo usan suites y pantallas que no lo montan).
 */
export function EscalafySection({clientId, clients = [], credentialId = null, onCredentialUsed, onChanged,
                                    onSyncingChange, onManageConnections}) {
    const {t, i18n} = useTranslation();
    const language = i18n && i18n.language;
    // Con qué se abre el asistente (`null` = cerrado). `n` cambia en cada apertura: es la `key` que lo
    // hace nacer de cero —paso, mapeo, errores— y no heredar la apertura anterior (R-ecofe-05).
    const [wizard, setWizard] = useState(null);
    const [connections, setConnections] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [syncing, setSyncing] = useState({});
    // La espera de una sincronización encolada (ver `SYNC_POLL_MS`).
    const [waiting, setWaiting] = useState(false);
    // Las conexiones de Escalafy que ya están en Conexiones (el paso 0 del asistente). Se piden recién al abrir el
    // asistente desde el botón general: `agency/connections/` arma la pantalla entera de Conexiones y la mayoría
    // de las visitas a Economía no abre el asistente. Si no se pueden leer, el asistente arranca por el OAuth:
    // no es un error de esta pantalla. `null` = todavía no se pidieron (o vienen en camino).
    const [agencyCredentials, setAgencyCredentials] = useState(null);
    const credentialsRequested = useRef(false);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    const loadAgencyCredentials = useCallback(() => {
        if (credentialsRequested.current) return;
        credentialsRequested.current = true;
        // «Una vez por pantalla» vale sólo para una respuesta buena: con un error, esta apertura va al OAuth y la
        // próxima vuelve a preguntar (si no, una agencia con Escalafy en Conexiones repetía el OAuth).
        const failed = () => {
            credentialsRequested.current = false;
            if (alive.current) setAgencyCredentials([]);
        };
        EscalafyChannelApi.agencyConnections()
            .then((response) => {
                if (!response || response.status !== 'OK') {
                    failed();
                    return;
                }
                if (alive.current) setAgencyCredentials(usableAgencyCredentials(response));
            })
            .catch(failed);
    }, []);

    const openWizard = useCallback((options = {}) => {
        // Sólo el botón general ofrece el paso 0; la vuelta del OAuth y la reconexión no lo usan.
        if (!options.credentialId && !options.reconnectCredentialId) loadAgencyCredentials();
        setWizard((current) => ({credentialId: null, reconnectCredentialId: null, ...options,
            n: ((current && current.n) || 0) + 1}));
    }, [loadAgencyCredentials]);

    useEffect(() => {
        if (!credentialId) return;
        openWizard({credentialId});
        if (onCredentialUsed) onCredentialUsed();
    }, [credentialId, onCredentialUsed, openWizard]);

    // Una sola espera a la vez: la nueva reemplaza a la anterior, y se corta al salir de la pantalla.
    const pollTimer = useRef(null);
    const connectionsRef = useRef(connections);
    connectionsRef.current = connections;
    const onChangedRef = useRef(onChanged);
    onChangedRef.current = onChanged;
    useEffect(() => () => clearTimeout(pollTimer.current), []);

    const onSyncingChangeRef = useRef(onSyncingChange);
    onSyncingChangeRef.current = onSyncingChange;
    useEffect(() => {
        if (onSyncingChangeRef.current) onSyncingChangeRef.current(waiting);
    }, [waiting]);

    // Terminó, pero no necesariamente bien. Un intento que falló por un estado (el plan, el rol) se dice con
    // el mismo texto que la fila; uno que falló por algo transitorio (cupo, red, sin datos) deja el estado en
    // OK y también se dice (R-escalafy-03). Sólo cuentan las conexiones que terminaron.
    const announce = useCallback((finished) => {
        const byStatus = finished.find((connection) => connectionStatusKey(connection));
        if (byStatus) {
            toast.warning(t(connectionStatusKey(byStatus)));
            return;
        }
        if (finished.some(lastAttemptFailed)) {
            toast.warning(t('economics.escalafy.sync_failed_retry'));
            return;
        }
        toast.success(t('economics.escalafy.synced'));
    }, [t]);

    // `ids`: las conexiones que se esperan (`null` = cualquiera, p. ej. después de vincular).
    const waitForSync = useCallback((stamps, ids = null) => {
        clearTimeout(pollTimer.current);
        setWaiting(true);
        let tries = 0;
        let latest = null;
        const stop = () => {
            setWaiting(false);
            if (!latest) return;
            setConnections(latest);
            // La franja del formulario leyó `sync_in_progress` al cargar la pantalla: que lo vuelva a leer.
            if (latest.some((connection) => connection.sync_in_progress !== undefined) && onChangedRef.current) {
                onChangedRef.current();
            }
        };
        const again = () => {
            const backendStillRunning = anyInProgress(latest);
            if (tries < SYNC_POLL_TRIES || (backendStillRunning && tries < SYNC_POLL_MAX_TRIES)) {
                pollTimer.current = setTimeout(tick, SYNC_POLL_MS);
                return;
            }
            stop();
        };
        const tick = () => {
            tries += 1;
            ClientEconomicsApi.escalafyConnections(clientId).then((response) => {
                if (!response || response.status !== 'OK') {
                    again();
                    return;
                }
                const list = ownConnections(response, clientId);
                const finished = finishedConnections(list, stamps, ids);
                if (!finished.length) {
                    latest = list;
                    again();
                    return;
                }
                setWaiting(false);
                setConnections(list);
                if (onChangedRef.current) onChangedRef.current();
                announce(finished);
            }).catch(again);
        };
        again();
    }, [clientId, announce]);

    const loadConnections = useCallback(() => {
        ClientEconomicsApi.escalafyConnections(clientId).then((response) => {
            const list = ownConnections(response, clientId);
            setConnections(list);
            // Una actualización que el backend sabe en curso (se pidió y se recargó la pantalla, o la pidió
            // otra pestaña): se la espera igual que a la que se pide desde acá.
            const running = list.filter((connection) => connection.sync_in_progress === true);
            if (running.length) waitForSync(syncStamps(list), running.map((connection) => connection.id));
        }).catch(() => setConnections([]));
    }, [clientId, waitForSync]);

    useEffect(() => { loadConnections(); }, [loadConnections]);

    const syncNow = (connection) => {
        if (syncing[connection.id] || waiting) return;
        setSyncing((current) => ({...current, [connection.id]: true}));
        const done = () => setSyncing((current) => ({...current, [connection.id]: false}));
        const stamps = syncStamps(connectionsRef.current);
        ClientEconomicsApi.escalafySync(connection.id).then((response) => {
            done();
            if (response && response.status === 'OK') {
                toast.success(t('economics.escalafy.sync_queued'));
                waitForSync(stamps, [connection.id]);
            } else {
                toast.error(syncErrorMessage(response, t));
            }
        }).catch(() => {
            done();
            toast.error(t('economics.error.generic'));
        });
    };

    const handleLinked = () => {
        const stamps = syncStamps(connectionsRef.current);
        loadConnections();
        if (onChanged) onChanged();
        waitForSync(stamps);
    };

    const disconnect = () => {
        const connection = confirm;
        setConfirm(null);
        if (!connection) return;
        ClientEconomicsApi.escalafyDisconnect(connection.id).then((response) => {
            if (!response || response.status !== 'OK') {
                toast.error(t('economics.error.generic'));
                return;
            }
            loadConnections();
            if (onChanged) onChanged();
        });
    };

    const isSyncing = (connection) => waiting || !!syncing[connection.id] || connection.sync_in_progress === true;

    const when = (iso) => syncWhenLabel(iso, t, language);

    const toneOf = (connection) => {
        if (isSyncing(connection)) return '-syncing';
        return connectionStatusKey(connection) ? '-warn' : '-ok';
    };

    const statusText = (connection) => {
        if (isSyncing(connection)) return t('economics.escalafy.conn.syncing');
        const key = connectionStatusKey(connection);
        if (key) return t(key);
        if (lastAttemptFailed(connection)) return t('economics.escalafy.conn.retry_pending');
        const fields = connection.derivation && Array.isArray(connection.derivation.fields)
            ? connection.derivation.fields.filter((field) => FORM_FIELDS.includes(field)) : null;
        if (!fields) return t('economics.escalafy.conn.ok');
        return t('economics.escalafy.conn.ok_count', {count: fields.length, total: FORM_FIELDS.length});
    };

    // La segunda línea: de cuándo es el último dato bueno y, si el último intento no lo movió (falló, por un
    // estado o por algo transitorio), cuándo fue ese intento.
    const metaText = (connection) => {
        if (isSyncing(connection)) return t('economics.escalafy.conn.syncing_meta');
        const last = connection.last_sync_at
            ? t('economics.escalafy.src.last', {when: when(connection.last_sync_at), interpolation: {escapeValue: false}})
            : t('economics.escalafy.conn.never');
        if (lastAttemptFailed(connection)) {
            return `${last} · ${t('economics.escalafy.conn.last_attempt', {
                when: when(connection.last_sync_attempt_at), interpolation: {escapeValue: false},
            })}`;
        }
        if (!connectionStatusKey(connection) && connection.last_sync_at) {
            return `${last} · ${t('economics.escalafy.conn.daily')}`;
        }
        return last;
    };

    const renderActions = (connection) => (
        <span className="eco-conn__acts">
            {connectionNeedsReconnect(connection) ? (
                <button type="button" className="eco-link" data-testid={`eco-escalafy-reconnect-${connection.id}`}
                        disabled={!connection.credential}
                        onClick={() => openWizard({reconnectCredentialId: connection.credential.id})}>
                    <Plugs size={13} weight="bold"/>
                    {t('economics.escalafy.reconnect')}
                </button>
            ) : (
                <button type="button" className="eco-link" data-testid={`eco-escalafy-sync-${connection.id}`}
                        disabled={isSyncing(connection)} aria-busy={isSyncing(connection) || undefined}
                        onClick={() => syncNow(connection)}>
                    <ArrowsClockwise size={13} weight="bold" className={isSyncing(connection) ? 'eco-spin' : undefined}/>
                    {t('economics.escalafy.sync_now')}
                </button>
            )}
            <button type="button" className="eco-link -muted" onClick={() => setConfirm(connection)}
                    data-testid={`eco-escalafy-${isChannelLink(connection) ? 'unlink' : 'disconnect'}-${connection.id}`}>
                {t(isChannelLink(connection) ? 'economics.escalafy.unlink' : 'economics.escalafy.disconnect')}
            </button>
        </span>
    );

    // El texto del modal es el de la conexión que se está quitando, también mientras se cierra (con
    // `confirm` ya en null, el título cambiaba a mitad de la animación).
    const confirmShown = useRef(null);
    if (confirm) confirmShown.current = confirm;
    const unlinking = isChannelLink(confirmShown.current);
    const goToConnections = (event) => {
        if (!onManageConnections) return;
        event.preventDefault();
        onManageConnections();
    };
    // La conexión vive en Conexiones desde que Escalafy es un canal: el link aparece cuando hay de qué hablar.
    const showConnectionsLink = (connections || []).some(isChannelLink);

    return (
        <section className="eco-fs eco-escalafy" id="escalafy" data-testid="eco-escalafy-section"
                 aria-labelledby="eco-escalafy-title">
            <div className="eco-fs__head">
                <h2 className="eco-fs__title" id="eco-escalafy-title">{t('economics.escalafy.title')}</h2>
            </div>
            <p className="eco-fs__help">{t('economics.escalafy.intro')}</p>

            {connections && connections.length > 0 && (
                <ul className="eco-conns">
                    {connections.map((connection) => (
                        <li className={`eco-conn ${toneOf(connection)}`} key={connection.id}
                            data-testid={`eco-escalafy-conn-${connection.id}`}>
                            <span className="eco-live" aria-hidden="true"/>
                            <span className="eco-conn__main" role={isSyncing(connection) ? 'status' : undefined}>
                                <span className="eco-conn__line">
                                    <span className="eco-conn__name">{connection.org_name}</span>{' '}
                                    <span className="eco-conn__status">{statusText(connection)}</span>
                                </span>
                                <span className="eco-conn__meta" data-testid={`eco-escalafy-meta-${connection.id}`}>
                                    {metaText(connection)}
                                </span>
                            </span>
                            {renderActions(connection)}
                        </li>
                    ))}
                </ul>
            )}

            <div className="eco-fs__acts">
                <button type="button" className="pnl-btn pnl-btn--ghost" onClick={() => openWizard()}
                        data-testid="eco-escalafy-open">
                    <Plugs size={14} weight="bold"/>
                    {t('client_panel.economy.empty.cta_escalafy')}
                </button>
                {showConnectionsLink && (
                    <a className="eco-link eco-escalafy__manage" href={AGENCY_CONNECTIONS_PATH} onClick={goToConnections}
                       data-testid="eco-escalafy-manage">
                        {t('economics.escalafy.manage_in_connections')}
                    </a>
                )}
            </div>

            {wizard && (
                <EscalafyConnectWizard
                    key={wizard.n}
                    isOpen
                    onClose={() => setWizard(null)}
                    clientId={clientId}
                    clients={clients}
                    credentialId={wizard.credentialId}
                    reconnectCredentialId={wizard.reconnectCredentialId}
                    agencyCredentials={agencyCredentials}
                    onLinked={handleLinked}
                />
            )}

            <YesNoModal
                isOpen={!!confirm}
                skipRenderButton={true}
                title={t(unlinking ? 'economics.escalafy.unlink' : 'economics.escalafy.disconnect')}
                message={t(unlinking ? 'economics.escalafy.unlink_confirm' : 'economics.escalafy.disconnect_confirm')}
                question=""
                onYesAnswer={disconnect}
                onNoAnswer={() => setConfirm(null)}
            />
        </section>
    );
}

export default EscalafyConnectWizard;
